import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  Divider, 
  CircularProgress,
  Avatar,
  useTheme,
  Paper,
  Grid,
  Tooltip
} from '@mui/material';
import { 
  Event as EventIcon,
  Today as TodayIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDisplay } from '../../components/PublicDisplayWrapper';
import { 
  format, 
  parseISO, 
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  addMonths,
  subMonths,
  isSameDay,
  isSameMonth,
  startOfDay,
  isBefore,
  startOfWeek,
  endOfWeek,
  addDays
} from 'date-fns';
import config from '../../config';

// Calendar styles similar to TimeOffApp
const calendarStyles = {
  calendar: {
    width: '100%',
    backgroundColor: '#fff',
    borderRadius: '8px',
  },
  header: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    padding: '8px',
    textAlign: 'center',
    backgroundColor: '#f5f5f5',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    borderBottom: '1px solid #ddd',
    '&:last-child': {
      borderBottom: 'none',
      borderBottomLeftRadius: '8px',
      borderBottomRightRadius: '8px',
    },
  },
  cell: {
    minHeight: '100px',
    padding: '8px',
    backgroundColor: '#fff',
    borderRight: '1px solid #ddd',
    '&:last-child': {
      borderRight: 'none',
    },
    '&.disabled': {
      opacity: 0.5,
    },
    '&.selected': {
      backgroundColor: '#f0f7ff',
    },
  },
  number: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: 'bold',
  },
};

function TimeOffDisplay({ isPublic = false, hardcodedToken = null }) {
  const { token } = useAuth();
  // Always call the hook, but only use its value if isPublic is true and no hardcoded token
  const publicDisplayContext = usePublicDisplay();
  // Use hardcoded token if provided, otherwise use the context token
  const publicToken = isPublic ? (hardcodedToken || publicDisplayContext.publicToken) : null;
  const theme = useTheme();
  
  // State for time off data
  const [timeOffs, setTimeOffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current time for the clock
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Current month for the calendar
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Update the time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Filter to current and future time offs
  const filterCurrentAndFutureTimeOffs = (timeOffs) => {
    if (!Array.isArray(timeOffs)) {
      console.error('Expected array for timeOffs, got:', typeof timeOffs, timeOffs);
      return [];
    }
    
    const today = startOfDay(new Date());
    
    return timeOffs.filter(timeOff => {
      try {
        if (!timeOff || !timeOff.startDate) {
          console.log('Skipping invalid time off entry:', timeOff);
          return false;
        }
        
        const startDate = parseISO(timeOff.startDate);
        
        // Include all time offs that are today or in the future
        return !isBefore(startDate, today);
      } catch (error) {
        console.error('Error filtering time off:', error, timeOff);
        return false;
      }
    });
  };
  
  // Consolidate consecutive time offs for the same user
  const consolidateTimeOffs = (timeOffs) => {
    if (!Array.isArray(timeOffs)) return [];
    
    // Ensure all time offs have the necessary data
    return timeOffs.map(timeOff => {
      // Log the raw time off entry for debugging
      console.log('Processing time off for consolidation:', timeOff);
      
      // Ensure we have user data
      if (!timeOff.user) {
        console.warn('Time off missing user data:', timeOff);
        
        // Try to extract user info from requestedBy if available
        if (timeOff.requestedBy) {
          timeOff.user = {
            _id: timeOff.requestedBy._id || timeOff.requestedBy,
            firstName: timeOff.requestedBy.firstName || 'Unknown',
            lastName: timeOff.requestedBy.lastName || 'User',
            color: timeOff.requestedBy.color || '#1976d2'
          };
        } else {
          // Default user data if none available
          timeOff.user = {
            _id: 'unknown',
            firstName: 'Unknown',
            lastName: 'User',
            color: '#1976d2'
          };
        }
      }
      
      // Ensure half day status is properly set
      // Check multiple possible fields that might indicate a half day
      timeOff.halfDay = 
        timeOff.halfDay === true || 
        timeOff.type === 'HALF_DAY' || 
        timeOff.type === 'HALF_DAY_AM' || 
        timeOff.type === 'HALF_DAY_PM';
      
      console.log('Processed time off entry:', {
        id: timeOff._id,
        user: `${timeOff.user.firstName} ${timeOff.user.lastName}`,
        halfDay: timeOff.halfDay,
        startDate: timeOff.startDate
      });
      
      return timeOff;
    });
  };

  // Fetch time offs from API
  const fetchTimeOffs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Use different endpoints based on whether this is public or authenticated
      let endpoint, headers;
      
      if (isPublic && publicToken) {
        endpoint = `${config.apiUrl}/public/timeoff/dashboard?token=${publicToken}`;
        headers = { 'Content-Type': 'application/json' };
        console.log('Using public endpoint for time off:', endpoint);
      } else {
        endpoint = `${config.apiUrl}/timeoff/dashboard`;
        headers = { 'Authorization': `Bearer ${token}` };
        console.log('Using authenticated endpoint for time off:', endpoint);
      }
      
      // Use the existing endpoint with authentication
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      
      // Check if data is an array before processing
      if (!Array.isArray(data)) {
        console.error('Expected array for time offs, got:', typeof data, data);
        throw new Error('Invalid data format received from API');
      }
      
      // Log raw data for debugging
      console.log('Raw time off data from API:', data);
      
      // Filter to current and future time offs
      const filteredTimeOffs = filterCurrentAndFutureTimeOffs(data);
      console.log('Filtered time offs:', filteredTimeOffs);
      
      // Process and consolidate time offs
      const processedTimeOffs = consolidateTimeOffs(filteredTimeOffs);
      console.log('Processed time offs:', processedTimeOffs);
      
      setTimeOffs(processedTimeOffs);
    } catch (error) {
      console.error('Error fetching time offs:', error);
      setError(error.message || 'Failed to load time off data');
      setTimeOffs([]); // Set empty array to prevent processing errors
    } finally {
      setLoading(false);
    }
  }, [token, isPublic, publicToken]);

  useEffect(() => {
    console.log('TimeOffDisplay component mounted');
    // Fetch data
    fetchTimeOffs();
    
    // Set up refresh interval (every 5 minutes)
    const refreshInterval = setInterval(() => {
      fetchTimeOffs();
    }, 5 * 60 * 1000);
    
    return () => {
      console.log('TimeOffDisplay component unmounting');
      clearInterval(refreshInterval);
    };
  }, [fetchTimeOffs]);

  // Navigate to previous month
  const goToPreviousMonth = () => {
    setCurrentMonth(prevMonth => subMonths(prevMonth, 1));
  };

  // Navigate to next month
  const goToNextMonth = () => {
    setCurrentMonth(prevMonth => addMonths(prevMonth, 1));
  };

  // Helper function to ensure valid date
  const ensureValidDate = (date) => {
    if (!date) return new Date();
    try {
      const d = new Date(date);
      return isNaN(d.getTime()) ? new Date() : d;
    } catch (error) {
      console.error('Invalid date:', date, error);
      return new Date();
    }
  };

  // Helper function to safely format date
  const safeFormat = (date, formatStr) => {
    try {
      const validDate = ensureValidDate(date);
      return format(validDate, formatStr);
    } catch (error) {
      console.error('Format error:', error);
      return '';
    }
  };

  // Get time offs for a specific day
  const getTimeOffsForDay = (day) => {
    if (!day) return [];
    
    return timeOffs.filter(timeOff => {
      try {
        const timeOffDate = parseISO(timeOff.startDate);
        return isSameDay(timeOffDate, day);
      } catch (error) {
        console.error('Error comparing dates:', error, 'for timeOff:', timeOff);
        return false;
      }
    });
  };

  // Render calendar using the same approach as TimeOffApp
  const renderCalendar = () => {
    try {
      // Get all dates in the current month
      const validCurrentMonth = ensureValidDate(currentMonth);
      const monthStart = startOfMonth(validCurrentMonth);
      const monthEnd = endOfMonth(validCurrentMonth);
      const startDate = startOfWeek(monthStart);
      const endDate = endOfWeek(monthEnd);
      const dateFormat = "d";
      const rows = [];

      let days = [];
      let day = startDate;

      // Create calendar rows
      while (day <= endDate) {
        for (let i = 0; i < 7; i++) {
          const cloneDay = new Date(day);
          const timeOffForDay = getTimeOffsForDay(cloneDay);

          days.push(
            <Box
              key={cloneDay.toISOString()}
              sx={{
                ...calendarStyles.cell,
                opacity: !isSameMonth(cloneDay, monthStart) ? 0.5 : 1,
                bgcolor: isSameDay(cloneDay, new Date()) ? '#f0f7ff' : '#fff',
              }}
            >
              <Typography sx={calendarStyles.number}>
                {safeFormat(cloneDay, dateFormat)}
              </Typography>
              {timeOffForDay.map((timeOff, index) => {
                // Check if it's a half day - check multiple possible fields
                const isHalfDay = timeOff.halfDay === true || 
                                 timeOff.type === 'HALF_DAY' || 
                                 timeOff.type === 'HALF_DAY_AM' || 
                                 timeOff.type === 'HALF_DAY_PM' || 
                                 false;
                
                return (
                  <Tooltip 
                    key={timeOff._id || index}
                    title={`${timeOff.user.firstName} ${timeOff.user.lastName}${isHalfDay ? ' (Half Day)' : ''}${timeOff.reason ? `: ${timeOff.reason}` : ''}`}
                    arrow
                  >
                    <Box 
                      sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        position: 'relative',
                        bgcolor: timeOff.user.color,
                        color: 'white',
                        borderRadius: 1,
                        p: 0.5,
                        mb: 0.5,
                        overflow: 'hidden',
                        fontSize: '0.75rem',
                        // Half day styling with gradient
                        ...(isHalfDay && {
                          background: `linear-gradient(to right, ${timeOff.user.color} 50%, rgba(255,255,255,0.7) 50%)`,
                          position: 'relative',
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0,0,0,0.1)',
                            zIndex: 0,
                            pointerEvents: 'none'
                          }
                        })
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <span>
                          {timeOff.user.firstName} {timeOff.user.lastName}{isHalfDay ? ' - Half Day' : ''}
                        </span>
                      </Box>
                      
                      {/* Note/Reason */}
                      {timeOff.reason && (
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            fontSize: '0.65rem',
                            fontStyle: 'italic',
                            opacity: 0.9,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            position: 'relative',
                            zIndex: 1
                          }}
                        >
                          {timeOff.reason}
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          );
          day = addDays(day, 1);
        }
        
        // Add the row of days to the rows array
        rows.push(
          <Box sx={calendarStyles.row} key={`row-${day.toISOString()}`}>
            {days}
          </Box>
        );
        
        // Reset days array for the next row
        days = [];
      }
      
      return rows;
    } catch (error) {
      console.error('Error rendering calendar:', error);
      return null;
    }
  };

  return (
    <Box sx={{ p: 2, height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header with current time */}
      <Paper 
        elevation={3}
        sx={{ 
          p: 2, 
          mb: 2, 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#2e7d32', // Green background
          color: 'white' // White text
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <EventIcon sx={{ mr: 1, color: 'white' }} /> {/* White icon */}
          <Typography variant="h4">
            Time Off Calendar
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="subtitle1">
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </Typography>
        </Box>
      </Paper>
      
      {/* Main content */}
      <Card 
        elevation={3}
        sx={{ 
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          borderTop: `6px solid ${theme.palette.secondary.main}`,
          overflow: 'hidden'
        }}
      >
        <CardHeader
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TodayIcon sx={{ mr: 1 }} />
                <Typography variant="h5">
                  {format(currentMonth, 'MMMM yyyy')}
                </Typography>
              </Box>
              <Box>
                <ArrowBackIcon 
                  sx={{ 
                    cursor: 'pointer', 
                    mr: 1.5,
                    '&:hover': { color: theme.palette.secondary.main }
                  }} 
                  onClick={goToPreviousMonth}
                />
                <ArrowForwardIcon 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { color: theme.palette.secondary.main }
                  }} 
                  onClick={goToNextMonth}
                />
              </Box>
            </Box>
          }
        />
        <Divider />
        <CardContent sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          p: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress size={60} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error" variant="h5">{error}</Typography>
            </Box>
          ) : timeOffs.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h5" color="textSecondary">
                No upcoming time off scheduled.
              </Typography>
            </Box>
          ) : (
            <Paper 
              sx={{
                ...calendarStyles.calendar,
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <Box sx={calendarStyles.header}>
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <Typography key={day} variant="subtitle2" fontWeight="bold">
                    {day}
                  </Typography>
                ))}
              </Box>
              <Box sx={{
                ...calendarStyles.body,
                flexGrow: 1
              }}>
                {renderCalendar()}
              </Box>
            </Paper>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}

export default TimeOffDisplay; 